// @ts-nocheck
import { useState } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Text,
  Group,
  TextInput,
  Button,
  SimpleGrid,
  Loader,
  Center,
  Divider,
} from '@mantine/core';
import { IconEdit, IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useCompany, useUpdateCompany } from '../../hooks/useCompany';

interface CompanyAddress {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface Company {
  id: string;
  name: string;
  legalName: string;
  ein: string;
  address: CompanyAddress;
  phone: string;
  website: string;
}

export function CompanySettingsPage() {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Company>>({});

  const { data: queryData, loading: isLoading } = useCompany();
  const [updateCompany, { loading: isSaving }] = useUpdateCompany();
  const companyResult = queryData as { company?: Company } | undefined;
  const company = companyResult?.company;

  const startEditing = () => {
    if (company) {
      setForm({ ...company });
      setEditing(true);
    }
  };

  const cancelEditing = () => {
    setEditing(false);
    setForm({});
  };

  const handleSave = async () => {
    try {
      await updateCompany({
        variables: {
          input: {
            name: form.name,
            legalName: form.legalName,
            ein: form.ein,
            phone: form.phone,
            website: form.website,
            address: form.address,
          },
        },
      });
      setEditing(false);
    } catch (error) {
      console.error('Failed to save company settings:', error);
    }
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      address: { ...(prev.address as CompanyAddress), [field]: value },
    }));
  };

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const displayData = editing ? form : company;

  return (
    <Stack gap="md">
      <PageHeader
        title="Company Settings"
        subtitle="View and manage company information"
        actions={
          editing ? (
            <Group gap="sm">
              <Button
                variant="subtle"
                icon={<IconX size={16} />}
                onClick={cancelEditing}
              >
                Cancel
              </Button>
              <Button
                icon={<IconDeviceFloppy size={16} />}
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                Save Changes
              </Button>
            </Group>
          ) : (
            <Button
              variant="light"
              icon={<IconEdit size={16} />}
              onClick={startEditing}
            >
              Edit
            </Button>
          )
        }
      />

      <Paper p="lg" withBorder>
        <Text size="lg" fw={600} mb="md">
          General Information
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {editing ? (
            <>
              <TextInput
                label="Company Name"
                value={form.name || ''}
                onChange={(e) => updateField('name', e.currentTarget.value)}
              />
              <TextInput
                label="Legal Name"
                value={form.legalName || ''}
                onChange={(e) => updateField('legalName', e.currentTarget.value)}
              />
              <TextInput
                label="EIN"
                value={form.ein || ''}
                onChange={(e) => updateField('ein', e.currentTarget.value)}
                placeholder="XX-XXXXXXX"
              />
              <TextInput
                label="Phone"
                value={form.phone || ''}
                onChange={(e) => updateField('phone', e.currentTarget.value)}
              />
              <TextInput
                label="Website"
                value={form.website || ''}
                onChange={(e) => updateField('website', e.currentTarget.value)}
              />
            </>
          ) : (
            <>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Company Name</Text>
                <Text size="sm" fw={500}>{displayData?.name || '-'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Legal Name</Text>
                <Text size="sm" fw={500}>{displayData?.legalName || '-'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">EIN</Text>
                <Text size="sm" fw={500}>{displayData?.ein || '-'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Phone</Text>
                <Text size="sm" fw={500}>{displayData?.phone || '-'}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Website</Text>
                <Text size="sm" fw={500}>{displayData?.website || '-'}</Text>
              </Group>
            </>
          )}
        </SimpleGrid>
      </Paper>

      <Paper p="lg" withBorder>
        <Text size="lg" fw={600} mb="md">
          Address
        </Text>
        {editing ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="Address Line 1"
              value={form.address?.line1 || ''}
              onChange={(e) => updateAddress('line1', e.currentTarget.value)}
            />
            <TextInput
              label="Address Line 2"
              value={form.address?.line2 || ''}
              onChange={(e) => updateAddress('line2', e.currentTarget.value)}
            />
            <TextInput
              label="City"
              value={form.address?.city || ''}
              onChange={(e) => updateAddress('city', e.currentTarget.value)}
            />
            <TextInput
              label="State"
              value={form.address?.state || ''}
              onChange={(e) => updateAddress('state', e.currentTarget.value)}
            />
            <TextInput
              label="ZIP Code"
              value={form.address?.zip || ''}
              onChange={(e) => updateAddress('zip', e.currentTarget.value)}
            />
            <TextInput
              label="Country"
              value={form.address?.country || ''}
              onChange={(e) => updateAddress('country', e.currentTarget.value)}
            />
          </SimpleGrid>
        ) : (
          <Stack gap="xs">
            {displayData?.address ? (
              <>
                <Text size="sm">
                  {displayData.address.line1}
                </Text>
                {displayData.address.line2 && (
                  <Text size="sm">{displayData.address.line2}</Text>
                )}
                <Text size="sm">
                  {displayData.address.city}, {displayData.address.state}{' '}
                  {displayData.address.zip}
                </Text>
                <Text size="sm">{displayData.address.country}</Text>
              </>
            ) : (
              <Text size="sm" c="dimmed">
                No address on file.
              </Text>
            )}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
